"use client"

import { useId } from "react"
import type { CSSProperties } from "react"
import { cn } from "./cn"
import styles from "./MinutHomeSensor.module.css"

/** M3 optical states plus an illustrative Seam monitoring state; not Seam API modes. */
export type MinutIndicator = "idle" | "monitoring" | "connecting" | "setup" | "armed" | "success" | "error" | "noise-alert" | "off"

export type MinutHomeSensorProps = {
  appearance?: "realistic" | "clay"
  indicator?: MinutIndicator
  /** Optical treatment only; soft uses a thinner seam light and a wider, fainter glow. */
  indicatorAppearance?: "standard" | "soft"
  displayPaused?: boolean
  disabled?: boolean
  onStatusCheck?: () => void
  className?: string
}

const BODY = "M220 35C352 35 400 77 400 217C400 357 355 399 220 399C85 399 40 357 40 217C40 77 88 35 220 35Z"
const FACE = "M220 41C349 41 394 82 394 217C394 352 351 393 220 393C89 393 46 352 46 217C46 82 91 41 220 41Z"
const LIGHT_COLORS: Record<MinutIndicator, string> = {
  idle: "#ffffff",
  monitoring: "var(--color-primary-400, #625ae2)",
  off: "#ffffff",
  connecting: "#ffffff",
  setup: "#9a74fa",
  armed: "#5b9bff",
  success: "#62d6a0",
  error: "#f06461",
  "noise-alert": "#f06461",
}
const STATE_LABELS: Record<MinutIndicator, string> = {
  idle: "Monitoring, status light off",
  monitoring: "Noise monitoring, illustrative purple status light",
  off: "Powered off",
  connecting: "Connecting, white circling light",
  setup: "Setup, purple circling light",
  armed: "Security status check, blue circling light",
  success: "Connected, green confirmation light",
  error: "Connection failed, red indicator",
  "noise-alert": "Noise warning, optional Flash and Sound light enabled",
}

// Shallow Fresnel facets catch light without making the PIR lens look like a display.
const LENS_FACETS = [
  {
    inner: 0, outer: 9, count: 6, offset: 0,
  },
  {
    inner: 9, outer: 20, count: 10, offset: 0.22,
  },
  {
    inner: 20, outer: 30, count: 14, offset: 0.08,
  },
].flatMap(({
  inner, outer, count, offset,
}, ring) => Array.from({ length: count }, (_, index) => {
  const start = index / count * Math.PI * 2 + offset
  const end = (index + 1) / count * Math.PI * 2 + offset
  const point = (angle: number, radius: number) => `${302 + Math.cos(angle) * radius},${137 + Math.sin(angle) * radius}`
  return {
    key: `${ring}-${index}`,
    points: `${point(start, inner)} ${point(start, outer)} ${point((start + end) / 2, outer + 0.4)} ${point(end, outer)} ${point(end, inner)}`,
    light: (index + ring * 3) % 3 !== 0,
    opacity: 0.055 + ((index * 7 + ring * 3) % 5) * 0.019,
  }
}))

/** Front view of the M3. Sensor readings belong to the host, not the blank plastic face. */
export function MinutHomeSensor({
  appearance = "realistic", indicator = "idle", indicatorAppearance = "standard", displayPaused = false,
  disabled = false, onStatusCheck, className,
}: MinutHomeSensorProps) {
  const id = useId().replace(/:/g, "")
  const ref = (name: string) => `url(#${id}-${name})`
  const clay = appearance === "clay"
  const circling = indicator === "connecting" || indicator === "setup" || indicator === "armed" || indicator === "monitoring"
  const lit = indicator !== "idle" && indicator !== "off"
  const description = `Minut Home Sensor, 3rd generation, ${clay ? "clay" : "white plastic"}. ${STATE_LABELS[indicator]}.`
  const interactive = Boolean(onStatusCheck) && !disabled && indicator !== "off"

  return (
    <div
      className={cn(styles.device, className)}
      data-minut-device={appearance}
      data-indicator={indicator}
      data-indicator-appearance={indicatorAppearance}
      data-paused={displayPaused}
      style={{ "--minut-light": LIGHT_COLORS[indicator] } as CSSProperties}
    >
      <svg
        className={styles.art}
        viewBox="0 0 440 440"
        role="img"
        aria-label={description}
      >
        <defs>
          <linearGradient
            id={`${id}-shell`}
            x1="0"
            y1="0"
            x2="0.85"
            y2="1"
          >
            <stop stopColor={clay ? "#f6f7f9" : "#ffffff"} />
            <stop
              offset="0.55"
              stopColor={clay ? "#dce1e8" : "#e5e7e6"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#bac2ce" : "#bcc1c0"}
            />
          </linearGradient>
          <radialGradient
            id={`${id}-face`}
            cx="0.25"
            cy="0.14"
            r="1.12"
          >
            <stop stopColor={clay ? "#f1f3f6" : "#f8f9f8"} />
            <stop
              offset="0.63"
              stopColor={clay ? "#e7ebf0" : "#ecefed"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#dce2e9" : "#dde1de"}
            />
          </radialGradient>
          <linearGradient
            id={`${id}-edge`}
            x1="0"
            y1="0"
            x2="0.7"
            y2="1"
          >
            <stop
              stopColor="#ffffff"
              stopOpacity="0.95"
            />
            <stop
              offset="0.45"
              stopColor="#ffffff"
              stopOpacity="0.3"
            />
            <stop
              offset="1"
              stopColor={clay ? "#bdc5d0" : "#b7bab7"}
              stopOpacity="0.75"
            />
          </linearGradient>
          <radialGradient
            id={`${id}-lens`}
            cx="0.37"
            cy="0.28"
            r="0.85"
          >
            <stop stopColor={clay ? "#e3e8ee" : "#e1e4e4"} />
            <stop
              offset="0.65"
              stopColor={clay ? "#d8dee7" : "#d5d9da"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#c4ccd7" : "#bdc3c5"}
            />
          </radialGradient>
          <linearGradient
            id={`${id}-lens-rim`}
            x1="0"
            y1="0"
            x2="0.7"
            y2="1"
          >
            <stop stopColor={clay ? "#bcc6d1" : "#a7adad"} />
            <stop
              offset="0.45"
              stopColor={clay ? "#dbe1e8" : "#d3d7d6"}
            />
            <stop
              offset="1"
              stopColor="#ffffff"
              stopOpacity="0.8"
            />
          </linearGradient>
          <filter
            id={`${id}-shadow`}
            x="-30%"
            y="-25%"
            width="160%"
            height="170%"
          >
            <feDropShadow
              dx="0"
              dy="16"
              stdDeviation="12"
              floodColor="#29313d"
              floodOpacity={clay ? "0.13" : "0.16"}
            />
            <feDropShadow
              dx="0"
              dy="3"
              stdDeviation="2.5"
              floodColor="#343c43"
              floodOpacity="0.11"
            />
          </filter>
          <filter
            id={`${id}-glow`}
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feGaussianBlur stdDeviation={indicatorAppearance === "soft" ? 5 : 3} />
          </filter>
        </defs>

        <g filter={ref("shadow")}>
          <path
            d={BODY}
            transform="translate(0 5)"
            fill={ref("shell")}
          />
          <path
            d={BODY}
            fill={ref("shell")}
            stroke={ref("edge")}
            strokeWidth="1.2"
          />
          <path
            d={FACE}
            transform="translate(0 0.6)"
            fill={clay ? "#aeb8c6" : "#767d7b"}
          />
          <path
            d={FACE}
            fill={ref("face")}
            transform="translate(220 217) scale(.993) translate(-220 -217)"
          />
          <path
            d={FACE}
            fill="none"
            stroke={ref("edge")}
            strokeWidth="1.3"
            transform="translate(220 217) scale(.987) translate(-220 -217)"
          />
        </g>

        <g
          className={styles.light}
          data-lit={lit}
          data-circling={circling}
        >
          <path
            d={FACE}
            className={styles.glow}
            pathLength="100"
            filter={ref("glow")}
          />
          <path
            d={FACE}
            className={styles.trail}
            pathLength="100"
          />
          <path
            d={FACE}
            className={styles.lead}
            pathLength="100"
          />
        </g>

        <circle
          cx="302"
          cy="138"
          r="34.5"
          fill="#ffffff"
          opacity={clay ? "0.3" : "0.6"}
        />
        <circle
          cx="302"
          cy="137"
          r="34"
          fill={ref("lens-rim")}
        />
        <circle
          cx="302"
          cy="137"
          r="32"
          fill={ref("lens")}
        />
        <g opacity={clay ? "0.35" : "0.55"}>
          {LENS_FACETS.map((facet) => (
            <polygon
              key={facet.key}
              points={facet.points}
              fill={facet.light ? "#ffffff" : "#89979f"}
              opacity={facet.opacity}
              stroke={facet.light ? "#ffffff" : "#89979f"}
              strokeWidth="0.35"
            />
          ))}
        </g>
        <circle
          cx="302"
          cy="137"
          r="31"
          fill="none"
          stroke="#ffffff"
          strokeWidth="1"
          opacity="0.23"
        />
      </svg>
      {onStatusCheck && (
        <button
          type="button"
          className={styles.statusButton}
          aria-label="Check Minut sensor connection status"
          disabled={!interactive}
          onClick={onStatusCheck}
        />
      )}
    </div>
  )
}
